/**
 * Locale-aware date/time formatters.
 *
 * Uses the i18n instance's current language (pt-BR or pt-PT) so dates always
 * render as dd/mm/yyyy, never as the US default 6/10/2026.
 *
 * Do NOT call toLocaleDateString() / toLocaleString() without a locale in
 * frontend/src — grep confirms this is the single authoritative call site
 * (exception: relativeTime.ts, which is intentionally locale-aware already).
 */
import i18n from '@/i18n';

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
};

const DATETIME_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};

function locale(): string {
  return i18n.language ?? 'pt-BR';
}

/**
 * Format an ISO date string as dd/mm/yyyy using the current i18n language.
 * Returns '—' for null, undefined, or invalid input.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  try {
    return d.toLocaleDateString(locale(), DATE_OPTIONS);
  } catch {
    return '—';
  }
}

/**
 * Format an ISO datetime string as dd/mm/yyyy hh:mm (24h) using the current
 * i18n language. Returns '—' for null, undefined, or invalid input.
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  try {
    return d.toLocaleString(locale(), DATETIME_OPTIONS);
  } catch {
    return '—';
  }
}

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * Format a captured `time` slot value (canonical HH:MM, 24h). Canonical times
 * are already friendly; this returns them unchanged. Returns '—' for null,
 * undefined, or any non-canonical input (never renders garbage). (F122)
 */
export function formatTime(value: string | null | undefined): string {
  if (!value) return '—';
  const s = value.trim();
  return TIME_RE.test(s) ? s : '—';
}
